import { useTranslation } from 'react-i18next';
import { HpCardHeader, HpTag, TableScroll } from '@hp/ui';
import { ErrorAlert } from '../../components/error-alert';
import { RelativeTime } from '../../components/relative-time';
import { PdLink } from '../../components/pd-link';
import { BrutTable, BrutTheadRow } from '../../components/ui/table';
import { display } from '../../lib/format';
import type { SelfSubmission } from './queries';

// --- Submissions ---

const CONSENT_CHANNEL_ORDER = ['email', 'sms', 'call'];

function ConsentChips({ consents }: { consents: Record<string, string> | null }) {
  const { t } = useTranslation('contactUpdates');
  if (!consents || Object.keys(consents).length === 0) return <span className="bo-muted">—</span>;
  const entries = Object.entries(consents).sort(
    ([a], [b]) => CONSENT_CHANNEL_ORDER.indexOf(a) - CONSENT_CHANNEL_ORDER.indexOf(b),
  );
  return (
    <span className="bo-chips">
      {entries.map(([channel, action]) => {
        const channelLabel = t(`submissions.channel.${channel}`, { defaultValue: channel });
        return action === 'granted' ? (
          <HpTag key={channel} tone="success">
            {t('submissions.granted', { channel: channelLabel })}
          </HpTag>
        ) : (
          <HpTag key={channel} tone="neutral" title={action}>
            {t('submissions.consentAction', { channel: channelLabel, action })}
          </HpTag>
        );
      })}
    </span>
  );
}

function SubmittedFields({ normalized }: { normalized: Record<string, unknown> }) {
  const entries = Object.entries(normalized).filter(([, v]) => v !== null && v !== undefined && v !== '');
  if (entries.length === 0) return <span className="bo-muted">—</span>;
  return (
    <>
      {entries.map(([key, value]) => (
        <div key={key} className="bo-mono">
          <span className="bo-muted">{key}:</span> {display(value)}
        </div>
      ))}
    </>
  );
}

export function SubmissionsTable({
  items,
  isLoading,
  error,
  pdDomain,
}: {
  items: SelfSubmission[];
  isLoading: boolean;
  error: unknown;
  pdDomain: string | null | undefined;
}) {
  const { t } = useTranslation(['contactUpdates', 'common']);
  return (
    <section className="hp-card">
      <HpCardHeader title={t('submissions.title')} subtitle={t('submissions.subtitle')} icon="mail" />
      <ErrorAlert error={error} className="bo-alert--inset" />
      <TableScroll label={t('submissions.tableLabel')}>
        <BrutTable>
          <BrutTheadRow>
            <th>{t('submissions.received')}</th>
            <th>{t('submissions.person')}</th>
            <th>{t('submissions.submittedFields')}</th>
            <th>{t('submissions.consents')}</th>
            <th>{t('submissions.campaign')}</th>
          </BrutTheadRow>
          <tbody>
            {items.map((sub: SelfSubmission) => (
              <tr key={sub.id}>
                <td className="bo-nowrap">
                  <RelativeTime value={sub.createdAt} />
                </td>
                <td className="bo-nowrap">
                  <span className="bo-row">
                    <strong>{sub.personName ?? '—'}</strong>
                    <PdLink domain={pdDomain} entity="person" pdId={sub.pdPersonId} />
                  </span>
                </td>
                <td>
                  <SubmittedFields normalized={sub.normalized} />
                </td>
                <td>
                  <ConsentChips consents={sub.consents} />
                </td>
                <td>
                  {sub.campaign ? <span className="bo-chip">{sub.campaign}</span> : <span className="bo-muted">—</span>}
                </td>
              </tr>
            ))}
            {!isLoading && items.length === 0 && (
              <tr>
                <td colSpan={5} className="bo-cell-empty">
                  {t('submissions.empty')}
                </td>
              </tr>
            )}
            {isLoading && (
              <tr>
                <td colSpan={5} className="bo-cell-empty">
                  {t('common:actions.loading')}
                </td>
              </tr>
            )}
          </tbody>
        </BrutTable>
      </TableScroll>
    </section>
  );
}
